import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';

import { CorsRecoveryOverlayService } from '@s/cors-recovery/cors-recovery-overlay.service';
import { CorsRecoveryStateService } from '@s/cors-recovery/cors-recovery-state.service';
import {
  CORS_RECOVERY_CLEAR_DELAY_MS,
  CORS_RECOVERY_CONSECUTIVE_THRESHOLD,
  CORS_RECOVERY_ERROR_WINDOW_MS,
} from '@s/cors-recovery/cors-recovery.constants';

@Injectable()
export class CorsErrorInterceptor implements HttpInterceptor, OnDestroy {
  private statusZeroTimestamps: number[] = [];
  private hasSeenStatusZeroSincePageLoad = false;
  private hasSeenSuccessSincePageLoad = false;
  private pendingRequestCount = 0;
  private clearRecoveryTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private corsRecoveryOverlayService: CorsRecoveryOverlayService,
    private corsRecoveryStateService: CorsRecoveryStateService,
    private ngZone: NgZone,
  ) {}

  public ngOnDestroy(): void {
    this.cancelPendingRecoveryClear();
  }

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    this.pendingRequestCount += 1;

    return next.handle(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          this.handleSuccess();
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 0) {
          this.handleStatusZeroError();
        }

        return throwError(() => error);
      }),
      finalize(() => this.handleRequestSettled()),
    );
  }

  private handleSuccess(): void {
    this.statusZeroTimestamps = [];
    this.hasSeenSuccessSincePageLoad = true;
  }

  private handleRequestSettled(): void {
    this.pendingRequestCount = Math.max(0, this.pendingRequestCount - 1);

    // Only consider recovery successful once the in-flight burst has settled.
    // Clearing on the first success would reset the attempt counter while other
    // parallel requests are still pending and about to fail with status 0.
    if (this.pendingRequestCount > 0) {
      return;
    }

    this.scheduleRecoveryClear();
  }

  private handleStatusZeroError(): void {
    this.hasSeenStatusZeroSincePageLoad = true;
    this.cancelPendingRecoveryClear();

    const now = Date.now();

    this.statusZeroTimestamps.push(now);
    this.statusZeroTimestamps = this.statusZeroTimestamps.filter(
      (timestamp) => now - timestamp <= CORS_RECOVERY_ERROR_WINDOW_MS,
    );

    const shouldShow =
      this.corsRecoveryStateService.hasActiveRecovery() ||
      this.statusZeroTimestamps.length >= CORS_RECOVERY_CONSECUTIVE_THRESHOLD;

    if (shouldShow && !this.corsRecoveryOverlayService.isVisible()) {
      this.corsRecoveryOverlayService.show();
    }
  }

  private scheduleRecoveryClear(): void {
    if (this.hasSeenStatusZeroSincePageLoad || !this.hasSeenSuccessSincePageLoad) {
      return;
    }

    if (!this.corsRecoveryStateService.hasActiveRecovery()) {
      return;
    }

    this.cancelPendingRecoveryClear();

    this.ngZone.runOutsideAngular(() => {
      this.clearRecoveryTimeoutId = setTimeout(() => {
        this.clearRecoveryTimeoutId = null;

        const canClear =
          this.corsRecoveryStateService.hasActiveRecovery() &&
          !this.hasSeenStatusZeroSincePageLoad &&
          this.pendingRequestCount === 0;

        if (canClear) {
          this.corsRecoveryStateService.clearState();
          this.corsRecoveryOverlayService.hide();
          window.location.reload();
        }
      }, CORS_RECOVERY_CLEAR_DELAY_MS);
    });
  }

  private cancelPendingRecoveryClear(): void {
    if (this.clearRecoveryTimeoutId !== null) {
      clearTimeout(this.clearRecoveryTimeoutId);
      this.clearRecoveryTimeoutId = null;
    }
  }
}
